<template>
  <v-row justify="center">
    <v-col cols="12" lg="10" class="admin-container">
      <!-- Search and Stats Card -->
      <v-card class="search-card" elevation="2">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="4" sm="6">
              <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="חיפוש מספר (מתחילת המספר)" outlined dense
                hide-details background-color="white" class="search-field" @input="handleSearchInput"></v-text-field>
            </v-col>
            <v-col cols="12" md="8" sm="6" class="d-flex align-center">
              <div class="subscribers-count">
                <v-icon color="#0d2c6d" class="ml-2">mdi-account-group</v-icon>
                <span>
                  סך הכל
                  <strong>{{ totalItems.toLocaleString() }}</strong>
                  מספרים
                </span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Subscribers Data Table Card -->
      <v-card class="data-table-card" elevation="2">
        <v-data-table id="table1" :headers="headers" :items="subscribers" class="subscribers-table"
          :loading="progressShow" loading-text="טוען נתונים..." no-data-text="אין עדיין מספרים" 
          :server-items-length="totalItems" :items-per-page="itemsPerPage" :page.sync="currentPage" @update:items-per-page="onItemsPerPageChange"
          :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @update:page="onPageChange" @update:sort-by="onSortChange"
          @update:sort-desc="onSortChange" :footer-props="{
            'items-per-page-options': [20, 50, 100, 500], 'items-per-page-text': 'שורות בעמוד:', showFirstLastPage: true,
            firstIcon: 'mdi-chevron-double-left',
            lastIcon: 'mdi-chevron-double-right',
            prevIcon: 'mdi-chevron-left',
            nextIcon: 'mdi-chevron-right'
          }">
          <!-- Custom headers -->
          <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
            v-slot:[`header.${header.value}`]="{ header }">
            <span class="table-header-text">{{ header.text }}</span>
          </template>

          <!-- Desktop rows -->
          <template v-if="$vuetify.breakpoint.mdAndUp" #item="{ item, index }">
            <tr class="table-row">
              <td class="table-cell">
                {{ (currentPage - 1) * itemsPerPage + index + 1 }}
              </td>
              <td class="table-cell">
                <div class="phone-number">
                  <v-icon small color="#0d2c6d" class="ml-1">mdi-phone</v-icon>
                  {{ item.phone }}
                </div>
              </td>
              <td class="table-cell">
                <v-chip small color="#6a6aec" text-color="white">
                  {{ item.line_number }}
                </v-chip>
              </td>
              <td class="table-cell">{{ item.lastCall || 'אין נתונים' }}</td>
              <td class="table-cell">
                <v-badge :content="item.calls_count" :value="item.calls_count" color="#0d2c6d" offset-x="0"
                  offset-y="10">
                  <v-icon>mdi-phone-in-talk</v-icon>
                </v-badge>
              </td>
            </tr>
          </template>

          <!-- No Data Template -->
          <template v-slot:no-data>
            <v-row v-if="progressShow" class="my-5">
              <v-col class="d-flex justify-center">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-col>
            </v-row>
            <div v-else class="empty-state">
              <v-icon size="64" color="#0d2c6d">mdi-account-off</v-icon>
              <span class="empty-text">אין עדיין מספרים</span>
              <v-btn color="primary" @click="refreshDetails()" class="mt-4">
                <v-icon left>mdi-refresh</v-icon>
                רענן
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </v-col>
  </v-row>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import Auth from '@/services/auth.service.js'
import utils from '@/util/utils'
import _ from 'lodash'

export default {
  components: {
    SnackBar,
  },
  data: () => ({
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    lineNumber: "",
    progressShow: false,
    search: "",
    subscribers: [],

    // server paging
    totalItems: 0,
    currentPage: 1,
    itemsPerPage: 50,
    sortBy: '',     // תוכל לשנות ל-field אחר אם קיים במסד
    sortDesc: true,

    headers: [
      { text: '#', value: "index", sortable: false, width: '50px' },
      { text: 'טלפון', align: 'center', value: 'phone' },
      { text: 'מחובר למערכת', value: 'line_number', align: 'center', width: '150px', sortable: false }, // אי אפשר למיין לפיו את כל הרשימה
      { text: 'חיוג אחרון', value: 'lastCall', align: 'center' },
      { text: 'כמות חיוגים', value: 'calls_count', width: '120px' },
    ],
  }),
  methods: {
    async getAllSubscribers(newSearch = false) {
      try {
        if (newSearch) {
          this.currentPage = 1;
        }

        this.progressShow = true;
        const token = localStorage.getItem("token");

        const query = new URLSearchParams({
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
          sortBy: this.sortBy || '',
          sortDesc: this.sortDesc ? 'desc' : 'asc',
          search: this.search || ''
        }).toString();

        const api = process.env.VUE_APP_BASE_URL + `/admin_api/get_all_subscribers?${query}`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar("שגיאה בקבלת נתונים", "red");
        } else if (res.status === 200) {
          // מצופה פורמט: { data: [...], totalItems: number }
          this.subscribers = jsonObject.data || [];
          this.totalItems = jsonObject.totalItems || 0;
          this.formatDetails();
        }
      } catch (error) {
        this.showSnackBar("Error get subscribes: " + error, "red");
      } finally {
        this.progressShow = false;
      }
    },

    formatDetails() {
      this.subscribers = this.subscribers.map((subscriber) => {
        return {
          ...subscriber,
          lastCall: subscriber.lastCall
            ? utils.getCurrentDate(subscriber.lastCall)
            : null,
        }
      });
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true;
    },

    refreshDetails() {
      this.getAllSubscribers();
    },

    // === אירועים של סרבר פייגינג ===
    onPageChange(newPage) {
      this.currentPage = newPage;
      this.getAllSubscribers();
    },

    onItemsPerPageChange(newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      this.getAllSubscribers(true);
    },

    onSortChange() {
      this.debouncedGetAllSubscribers(true);
    },

    handleSearchInput() {
      if (this.search) {
        this.debouncedGetAllSubscribers(true);
      } else {
        this.getAllSubscribers(true);
      }
    }
  },
  mounted() {
    this.lineNumber = Auth.getLineNumber();
    this.getAllSubscribers();
    this.debouncedGetAllSubscribers = _.debounce(this.getAllSubscribers, 700);
  },
}
</script>


<style scoped>
.admin-container {
  font-family: 'Heebo', sans-serif;
}

.search-card {
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.search-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.search-field {
  border-radius: 8px;
}

.subscribers-count {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #0d2c6d;
}

.data-table-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  margin-bottom: 40px;
}

.data-table-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.subscribers-table {
  width: 100%;
}

.table-header-text {
  font-size: 16px;
  font-weight: 600;
  color: #0d2c6d;
}

.table-row {
  height: 60px !important;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: rgba(13, 44, 109, 0.05) !important;
}

.table-cell {
  font-size: 14px;
  color: #333;
  text-align: center !important;
}

.phone-number {
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.empty-text {
  font-size: 20px;
  font-weight: 600;
  color: #0d2c6d;
  margin-top: 16px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .admin-container {
    padding: 0 12px;
  }

  .table-header-text {
    font-size: 14px;
  }

  .table-cell {
    font-size: 13px;
  }
}

@media (max-width: 600px) {

  .search-card,
  .data-table-card {
    border-radius: 8px;
  }

  .subscribers-count {
    justify-content: center;
    margin-top: 8px;
  }
}

#table1.v-data-table>>>.v-data-table-header th {
  background-color: rgba(0, 0, 0, 0.116) !important;
  border-left: 1px solid white !important;
}
</style>